import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../context/contextAuth.js";
import {
  getConfigAssets,
  getConfigScanProgress,
} from "../../../api/get/oci/get-config.js";
import { scanConfig } from "../../../api/post/post-oci.js";

const useOciConfig = () => {
  const { user, userId } = useAuth();
  const [tenancyId, setTenancyId] = useState("");
  const [ociUserId, setOciUserId] = useState("");
  const [fingerprint, setFingerprint] = useState("");
  const [region, setRegion] = useState("");
  const [compartmentId, setCompartmentId] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [privateKeyFileName, setPrivateKeyFileName] = useState("");
  const [passphrase, setPassphrase] = useState("");

  const [configLoading, setConfigLoading] = useState(false);
  const [configScanLoading, setConfigScanLoading] = useState(false);
  const [configScanProgress, setConfigScanProgress] = useState(null);
  const [configView, setConfigView] = useState(false);
  const [configAssets, setConfigAssets] = useState([]);
  const [cOciConfigAll, setCOciConfigAll] = useState(null);
  const [saveScore, setSaveScore] = useState(0);
  const [error, setError] = useState(null);

  const fetchConfigAssets = useCallback(async (requestedUserId) => {
    try {
      const assets = await getConfigAssets(requestedUserId);
      setConfigAssets(assets || []);
      setCOciConfigAll(assets?.[0]?.misconfig || null);
      setSaveScore(assets?.[0]?.ss ?? 0);
      return assets;
    } catch (fetchError) {
      console.error("Error loading OCI config assets:", fetchError);
      setConfigAssets([]);
      setCOciConfigAll(null);
      setSaveScore(0);
      throw fetchError;
    }
  }, []);

  useEffect(() => {
    if (!user || !userId) return;
    (async () => {
      try {
        setConfigLoading(true);
        await fetchConfigAssets(userId);
      } catch (fetchError) {
        console.error("Error in OCI initData:", fetchError);
      } finally {
        setConfigLoading(false);
      }
    })();
  }, [user, userId, fetchConfigAssets]);

  const initData = async () => {
    if (!user || !userId) return;
    return fetchConfigAssets(userId);
  };

  const refreshLatestAssessment = async (expectedScanId) => {
    let latestAssets = [];
    for (let attempt = 0; attempt < 5; attempt += 1) {
      latestAssets = await fetchConfigAssets(userId);
      if (!expectedScanId || latestAssets?.[0]?.oci_uuid === expectedScanId) {
        return latestAssets;
      }
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    throw new Error(
      "The OCI assessment completed, but its newest result is not available yet"
    );
  };

  const handlePrivateKeyFile = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setError(null);
    try {
      if (file.size > 64 * 1024) {
        throw new Error("The OCI API signing key file is too large");
      }
      const content = (await file.text()).trim();
      if (
        !/-----BEGIN (?:RSA |ENCRYPTED )?PRIVATE KEY-----/.test(content) ||
        !/-----END (?:RSA |ENCRYPTED )?PRIVATE KEY-----/.test(content)
      ) {
        throw new Error("Select a valid OCI API signing private-key PEM file");
      }

      setPrivateKey(content);
      setPrivateKeyFileName(file.name);
    } catch (fileError) {
      setPrivateKey("");
      setPrivateKeyFileName("");
      setError({
        message: fileError.message || "Unable to read the OCI private key",
        card: false,
      });
    } finally {
      if (event?.target) event.target.value = "";
    }
  };

  const handleConfigScan = async (event) => {
    if (event) event.preventDefault();
    setConfigScanLoading(true);
    setError(null);
    setConfigScanProgress({
      status: "running",
      stage: "starting",
      progress: 1,
      message: "Starting OCI assessment",
    });

    const progressTimer = window.setInterval(async () => {
      try {
        const progress = await getConfigScanProgress(userId);
        if (progress) setConfigScanProgress(progress);
      } catch (progressError) {
        console.warn("Unable to refresh OCI scan progress:", progressError);
      }
    }, 1000);

    try {
      if (!userId) {
        throw new Error("User ID does not exist. Please sign in again.");
      }
      if (
        !tenancyId ||
        !ociUserId ||
        !fingerprint ||
        !region ||
        !privateKey
      ) {
        throw new Error(
          "Provide the OCI tenancy, user, fingerprint, region, and API signing key"
        );
      }

      const result = await scanConfig(userId, {
        tenancyId,
        userId: ociUserId,
        fingerprint,
        region,
        compartmentId,
        privateKey,
        passphrase,
      });
      const finalProgress = await getConfigScanProgress(userId);
      if (finalProgress) setConfigScanProgress(finalProgress);
      await refreshLatestAssessment(result?.data?.scanId);
      setConfigView(true);
    } catch (scanError) {
      setConfigView(false);
      setError({
        message:
          scanError.message ||
          "OCI assessment failed. Check the API key and IAM policies.",
        card: scanError.card || false,
      });
    } finally {
      window.clearInterval(progressTimer);
      setConfigScanLoading(false);
      setPrivateKey("");
      setPrivateKeyFileName("");
      setPassphrase("");
      setTimeout(() => setError(null), 7000);
    }
  };

  const resetState = () => {
    setTenancyId("");
    setOciUserId("");
    setFingerprint("");
    setRegion("");
    setCompartmentId("");
    setPrivateKey("");
    setPrivateKeyFileName("");
    setPassphrase("");
    setConfigLoading(false);
    setConfigScanLoading(false);
    setConfigScanProgress(null);
    setConfigView(false);
    setConfigAssets([]);
    setCOciConfigAll(null);
    setSaveScore(0);
    setError(null);
  };

  return {
    tenancyId,
    setTenancyId,
    ociUserId,
    setOciUserId,
    fingerprint,
    setFingerprint,
    region,
    setRegion,
    compartmentId,
    setCompartmentId,
    privateKey,
    setPrivateKey,
    privateKeyFileName,
    setPrivateKeyFileName,
    passphrase,
    setPassphrase,
    configLoading,
    setConfigLoading,
    configScanLoading,
    setConfigScanLoading,
    configScanProgress,
    setConfigScanProgress,
    configView,
    setConfigView,
    configAssets,
    setConfigAssets,
    cOciConfigAll,
    setCOciConfigAll,
    saveScore,
    setSaveScore,
    error,
    setError,
    handleConfigScan,
    handlePrivateKeyFile,
    fetchConfigAssets,
    initData,
    resetState,
  };
};

export default useOciConfig;
